import { useCallback, useEffect, useState } from 'react';
import { listAllItems, listGroups, listItems } from '../data/catalog.ts';
import { listProgress } from '../data/progress.ts';
import type { CatalogGroup, CatalogItem, ProgressEntry } from '../data/db.ts';

/**
 * Gruppen fuer die Kinderansicht.
 *
 * Leere Gruppen werden ausgeblendet: eine Kachel ohne Folgen ist fuer das
 * Kind eine Sackgasse, die es nicht selbst aufloesen kann.
 */
export function useGroups(): { groups: CatalogGroup[]; reload: () => void } {
  const [groups, setGroups] = useState<CatalogGroup[]>([]);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([listGroups(), listAllItems()]).then(([allGroups, allItems]) => {
      if (cancelled) return;
      const gefuellt = new Set(allItems.map((item) => item.groupId));
      setGroups(allGroups.filter((group) => gefuellt.has(group.id)));
    });
    return () => {
      cancelled = true;
    };
  }, [version]);

  return { groups, reload: useCallback(() => setVersion((v) => v + 1), []) };
}

export function useItems(groupId: string | null): { items: CatalogItem[]; reload: () => void } {
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!groupId) {
      setItems([]);
      return;
    }
    let cancelled = false;
    void listItems(groupId).then((result) => {
      if (!cancelled) setItems(result);
    });
    return () => {
      cancelled = true;
    };
  }, [groupId, version]);

  return { items, reload: useCallback(() => setVersion((v) => v + 1), []) };
}

/** Fortschritt aller Folgen als Nachschlagetabelle nach itemId. */
export function useProgressMap(refreshKey: unknown): Map<string, ProgressEntry> {
  const [map, setMap] = useState<Map<string, ProgressEntry>>(new Map());

  useEffect(() => {
    let cancelled = false;
    void listProgress().then((entries) => {
      if (cancelled) return;
      setMap(new Map(entries.map((entry) => [entry.itemId, entry])));
    });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  return map;
}
